import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots, faPaperPlane, faXmark, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { useTranslation } from 'react-i18next';
import useUserSession from '../useUserSession';
import './ChatBot.scss';

// Configurer marked pour des sorties compactes
marked.setOptions({ breaks: true, gfm: true });

const CHATBOT_URL = 'https://grouping.binaire-backend.tech';
const RESPONSE_TIMEOUT = 30000;

function ChatBot() {
  const { t } = useTranslation();
  const { user } = useUserSession();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);

  const socket = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);

  const suggestions = [
    t('chatbot.suggestion1'),
    t('chatbot.suggestion2'),
    t('chatbot.suggestion3'),
    t('chatbot.suggestion4'),
  ];

  // Message de bienvenue initial
  useEffect(() => {
    setMessages([{ text: t('chatbot.welcome'), sender: 'bot', isWelcome: true }]);
  }, [t]);

  // Parser une réponse bot en HTML sécurisé
  const parseBotMessage = (response) => {
    const rawHtml = marked.parse(response || '');
    return DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: ['strong', 'em', 'b', 'i', 'br', 'p', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'a', 'code'],
      ALLOWED_ATTR: ['href', 'target'],
    });
  };

  // Charger l'historique des conversations via HTTP
  const loadChatHistory = async (userId) => {
    try {
      const res = await fetch(`${CHATBOT_URL}/messages/${userId}`);
      if (!res.ok) return;
      const history = await res.json();
      if (!Array.isArray(history) || history.length === 0) return;
      const parsed = history.map((msg) => {
        const sender = msg.role === 'assistant' ? 'bot' : 'user';
        // Retirer le préfixe [CONTEXTE: ...] des messages utilisateur
        const content = sender === 'user'
          ? msg.content.replace(/^\[CONTEXTE:[^\]]*\]\n?/, '')
          : msg.content;
        return {
          text: sender === 'bot' ? parseBotMessage(content) : content,
          sender,
          isHtml: sender === 'bot',
        };
      });
      setMessages([{ text: t('chatbot.welcome'), sender: 'bot', isWelcome: true }, ...parsed]);
      setShowSuggestions(false);
    } catch (err) {
      // Silencieux — l'historique n'est pas critique
    }
  };

  // Connexion Socket.IO — se reconnecte quand user change (login/logout)
  useEffect(() => {
    if (socket.current) {
      socket.current.disconnect();
      socket.current = null;
    }

    const userId = user?.uid || null;

    // Réinitialiser les messages à la déconnexion (pas d'historique hors connexion)
    setMessages([{ text: t('chatbot.welcome'), sender: 'bot', isWelcome: true }]);
    setShowSuggestions(true);

    socket.current = io(CHATBOT_URL, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
      timeout: 10000,
    });

    socket.current.on('connect', () => {
      setIsConnected(true);
      // Enregistrer l'utilisateur auprès du backend
      if (userId) {
        socket.current.emit('register', userId);
        loadChatHistory(userId);
      }
    });

    socket.current.on('disconnect', () => {
      setIsConnected(false);
    });

    socket.current.on('connect_error', () => {
      setIsConnected(false);
    });

    socket.current.on('chat_message', (response) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      const sanitized = parseBotMessage(response);
      setMessages((prev) => [...prev, { text: sanitized, sender: 'bot', isHtml: true }]);
      setIsLoading(false);
    });

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (socket.current) {
        socket.current.disconnect();
        socket.current = null;
      }
    };
  }, [user?.uid, t]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const sendMessage = (text) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    setMessages((prev) => [...prev, { text: trimmed, sender: 'user' }]);
    setInput('');
    setIsLoading(true);
    setShowSuggestions(false);

    if (!socket.current?.connected) {
      setMessages((prev) => [...prev, { text: t('chatbot.connectionError'), sender: 'bot', isError: true }]);
      setIsLoading(false);
      return;
    }

    // Préfixer le contexte utilisateur (plateforme + connexion) pour l'IA
    const ctx = `[CONTEXTE: plateforme=web, connecte=${user ? 'oui' : 'non'}${user?.name ? ', utilisateur=' + user.name : ''}]`;
    socket.current.emit('chat_message', `${ctx}\n${trimmed}`);

    timeoutRef.current = setTimeout(() => {
      setMessages((prev) => [...prev, { text: t('chatbot.timeout'), sender: 'bot', isError: true }]);
      setIsLoading(false);
    }, RESPONSE_TIMEOUT);
  };

  const handleSend = () => sendMessage(input);

  const handleSuggestionClick = (suggestion) => sendMessage(suggestion);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleRetry = () => {
    if (socket.current) {
      socket.current.disconnect();
      socket.current.connect();
    }
  };

  return (
    <div className="ChatBot">
      {isOpen && (
        <div className="ChatBot_window">
          <div className="ChatBot_header">
            <div className="ChatBot_header_left">
              <span className="ChatBot_header_title">{t('chatbot.title')}</span>
              <span className={`ChatBot_header_status ${isConnected ? 'ChatBot_header_status--online' : ''}`}>
                {isConnected ? t('chatbot.online') : t('chatbot.offline')}
              </span>
            </div>
            <button className="ChatBot_header_close" onClick={() => setIsOpen(false)}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className="ChatBot_messages">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`ChatBot_message ChatBot_message--${msg.sender}${msg.isError ? ' ChatBot_message--error' : ''}`}
                {...(msg.isHtml ? { dangerouslySetInnerHTML: { __html: msg.text } } : { children: msg.text })}
              />
            ))}

            {showSuggestions && messages.length <= 1 && (
              <div className="ChatBot_suggestions">
                {suggestions.map((s, i) => (
                  <button key={i} className="ChatBot_suggestion" onClick={() => handleSuggestionClick(s)}>
                    {s}
                  </button>
                ))}
              </div>
            )}

            {isLoading && (
              <div className="ChatBot_message ChatBot_message--bot">
                <div className="ChatBot_typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {!isConnected && (
            <div className="ChatBot_reconnect">
              <span>{t('chatbot.disconnected')}</span>
              <button onClick={handleRetry}>
                <FontAwesomeIcon icon={faRotateRight} /> {t('chatbot.retry')}
              </button>
            </div>
          )}

          <div className="ChatBot_input">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('chatbot.placeholder')}
              disabled={isLoading || !isConnected}
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !input.trim() || !isConnected}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
        </div>
      )}

      <button
        className={`ChatBot_toggle ${isOpen ? 'ChatBot_toggle--open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <FontAwesomeIcon icon={isOpen ? faXmark : faCommentDots} />
      </button>
    </div>
  );
}

export default ChatBot;
